import React from 'react';

export interface PillarScopeChipProps {
  pillars: string[] | null | undefined;
  className?: string;
}

/**
 * PillarScopeChip — CT-P P.3 scope indicator. Enumerates the case's damage
 * pillars when scope is anything other than the default single BI pillar.
 *
 * A pillar is SCOPE, not a verification state: neutral tokens ONLY. Never
 * borrows --color-verified / --color-needs-human / --color-blocker /
 * --color-draft, and never the brand accent. (K3 ruling 2026-09-02.)
 *
 * The chip enumerates the full scope ("BI + PROPERTY") so additivity of the
 * damage tracks is explicit — never a lone "PROPERTY" tag that could be misread
 * as a posture. Dollar figures are NEVER summed across pillars; each is a
 * separate recovery track (surfaced with its own basis word elsewhere).
 */
export function PillarScopeChip({ pillars, className }: PillarScopeChipProps) {
  if (!pillars || pillars.length === 0) return null;
  // Default scope — the 95% single-BI case renders zero DOM (no clutter). A
  // property-only case (["property"]) is NOT the default and still renders.
  if (pillars.length === 1 && pillars[0] === 'bi') return null;

  const label = pillars.map((p) => p.toUpperCase()).join(' + ');

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-[3px] border border-[var(--color-border)] text-[10px] font-mono uppercase tracking-[0.08em] text-[var(--color-text-secondary)] whitespace-nowrap select-none cursor-default ${className || ''}`}
      title={`Damage pillars in scope: ${label}. Each pillar is a separate recovery track — dollar figures are never summed across pillars.`}
      aria-label={`Case scope: ${label}. Separate recovery tracks; dollar figures are never combined.`}
      data-testid="pillar-scope-chip"
    >
      {/* No leading status dot — a pillar is SCOPE, not a verification state; the dot
          would borrow the status-chip affordance and invite a status reading (K3 P2). */}
      <span>{label}</span>
    </span>
  );
}

export default PillarScopeChip;
